import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Result,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { CloudUploadOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  adminService,
  BasePriceDefaults,
  BasePriceDiffRow,
  BasePriceSummary,
  BasePriceValidationResponse,
  RowError,
} from '../../services/adminService';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

const BRAND = '#053057';

const DIM_COLUMNS: ColumnsType<BasePriceDiffRow> = [
  { title: 'Resource Type', dataIndex: 'resource_type', key: 'resource_type' },
  { title: 'Region', dataIndex: 'region', key: 'region' },
  { title: 'Service Line', dataIndex: 'service_line', key: 'service_line' },
  { title: 'Function', dataIndex: 'function', key: 'function' },
  { title: 'Career Level', dataIndex: 'career_level', key: 'career_level' },
];

const fmt = (v: number | boolean | null | undefined) =>
  v === null || v === undefined ? '∅' : String(v);

// Dimension columns + the row's actual values (for added / deleted rows).
const VALUE_COLUMNS: ColumnsType<BasePriceDiffRow> = [
  ...DIM_COLUMNS,
  { title: 'USD', key: 'usd', align: 'right', render: (_, r) => fmt(r.values?.cost_per_hour_usd) },
  { title: 'GBP', key: 'gbp', align: 'right', render: (_, r) => fmt(r.values?.cost_per_hour_gbp) },
  { title: 'EUR', key: 'eur', align: 'right', render: (_, r) => fmt(r.values?.cost_per_hour_eur) },
  { title: 'Burden', key: 'burden', align: 'right', render: (_, r) => fmt(r.values?.burden_load_rate) },
  { title: 'Margin', key: 'margin', align: 'right', render: (_, r) => fmt(r.values?.target_client_margin) },
  { title: 'Min FTE', key: 'minfte', align: 'right', render: (_, r) => fmt(r.values?.min_fte_allocation) },
  { title: 'Cost Editable', key: 'coste', render: (_, r) => fmt(r.values?.cost_per_hour_editable) },
  { title: 'Margin Editable', key: 'marge', render: (_, r) => fmt(r.values?.target_client_margin_editable) },
];

const EDITED_COLUMNS: ColumnsType<BasePriceDiffRow> = [
  ...DIM_COLUMNS,
  {
    title: 'Changes',
    key: 'changes',
    render: (_, row) => (
      <Space direction="vertical" size={0}>
        {row.changed_fields.map(c => (
          <Text key={c.field} style={{ fontSize: 12 }}>
            <Text code>{c.field}</Text>: {String(c.old ?? '∅')} → <Text strong>{String(c.new ?? '∅')}</Text>
          </Text>
        ))}
      </Space>
    ),
  },
];

const ROW_ERROR_COLUMNS: ColumnsType<RowError> = [
  { title: 'Row', dataIndex: 'row_number', key: 'row_number', width: 70 },
  { title: 'Resource Type', dataIndex: 'resource_type', key: 'resource_type' },
  { title: 'Region', dataIndex: 'region', key: 'region' },
  { title: 'Service Line', dataIndex: 'service_line', key: 'service_line' },
  { title: 'Function', dataIndex: 'function', key: 'function' },
  { title: 'Career Level', dataIndex: 'career_level', key: 'career_level' },
  { title: 'Problem', dataIndex: 'message', key: 'message' },
];

const summaryRowKey = (r: BasePriceDiffRow, idx?: number) =>
  `${r.resource_type}|${r.region}|${r.service_line}|${r.function}|${r.career_level}|${idx ?? 0}`;

const BasePriceVersionsTab: React.FC = () => {
  const [current, setCurrent] = useState(0); // step index
  const [defaults, setDefaults] = useState<BasePriceDefaults | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [eurCcr, setEurCcr] = useState<number | undefined>(undefined);
  const [gbpCcr, setGbpCcr] = useState<number | undefined>(undefined);
  const [compareCurrencies, setCompareCurrencies] = useState(false);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [validation, setValidation] = useState<BasePriceValidationResponse | null>(null);
  const [committed, setCommitted] = useState<{ version_id: number; version_name: string } | null>(null);
  const [form] = Form.useForm();

  const selectedFile: File | undefined = fileList[0]?.originFileObj as File | undefined;

  useEffect(() => {
    (async () => {
      try {
        const d = await adminService.getBasePriceVersionDefaults();
        setDefaults(d);
        setEurCcr(d.prev_usd_to_eur_ccr ?? undefined);
        setGbpCcr(d.prev_usd_to_gbp_ccr ?? undefined);
        form.setFieldsValue({ version_name: d.default_version_name });
      } catch (error: any) {
        message.error(error.message || 'Failed to load defaults');
      }
    })();
  }, [form]);

  const resetAll = () => {
    setCurrent(0);
    setFileList([]);
    setValidation(null);
    setCommitted(null);
    setCompareCurrencies(false);
    if (defaults) {
      setEurCcr(defaults.prev_usd_to_eur_ccr ?? undefined);
      setGbpCcr(defaults.prev_usd_to_gbp_ccr ?? undefined);
      form.setFieldsValue({ version_name: defaults.default_version_name });
    }
  };

  const runValidate = async (compareFlag: boolean, advance: boolean) => {
    if (!selectedFile) {
      message.warning('Please select a file first.');
      return;
    }
    if (eurCcr == null || gbpCcr == null) {
      message.warning('Enter both currency conversion rates.');
      return;
    }
    setValidating(true);
    try {
      const result = await adminService.validateBasePriceVersion(selectedFile, {
        usd_to_eur_ccr: eurCcr,
        usd_to_gbp_ccr: gbpCcr,
        compare_currencies: compareFlag,
      });
      setValidation(result);
      if (advance) setCurrent(1);
    } catch (error: any) {
      message.error(error.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleToggleCompare = async (checked: boolean) => {
    setCompareCurrencies(checked);
    await runValidate(checked, false); // re-validate in place, stay on Review
  };

  const handleCommit = async () => {
    if (!selectedFile || !validation) return;
    if (eurCcr == null || gbpCcr == null) return;
    let values: { version_name: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setCommitting(true);
    try {
      const result = await adminService.commitBasePriceVersion(selectedFile, {
        version_name: values.version_name,
        usd_to_eur_ccr: eurCcr,
        usd_to_gbp_ccr: gbpCcr,
        file_sha256: validation.file_sha256,
      });
      setCommitted(result);
      setCurrent(3);
      message.success(`Created base price version "${result.version_name}"`);
    } catch (error: any) {
      message.error(error.message || 'Failed to create version');
    } finally {
      setCommitting(false);
    }
  };

  const renderSummary = (summary: BasePriceSummary) => (
    <Collapse
      defaultActiveKey={['added', 'edited', 'deleted']}
      items={[
        {
          key: 'added',
          label: <span><Tag color="green">Added</Tag> {summary.added_count} row(s)</span>,
          children: (
            <Table
              size="small"
              scroll={{ x: 'max-content' }}
              columns={VALUE_COLUMNS}
              dataSource={summary.added}
              rowKey={summaryRowKey}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
            />
          ),
        },
        {
          key: 'edited',
          label: <span><Tag color="blue">Edited</Tag> {summary.edited_count} row(s)</span>,
          children: (
            <Table
              size="small"
              columns={EDITED_COLUMNS}
              dataSource={summary.edited}
              rowKey={summaryRowKey}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
            />
          ),
        },
        {
          key: 'deleted',
          label: <span><Tag color="red">Deleted</Tag> {summary.deleted_count} row(s)</span>,
          children: (
            <Table
              size="small"
              scroll={{ x: 'max-content' }}
              columns={VALUE_COLUMNS}
              dataSource={summary.deleted}
              rowKey={summaryRowKey}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
            />
          ),
        },
      ]}
    />
  );

  const renderReview = () => {
    if (!validation) return null;

    if (!validation.is_valid) {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="error"
            showIcon
            message="The file has problems and cannot be used"
            description="Fix the items below in your exported file, then upload and validate again."
          />
          {validation.header_errors.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Header errors"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {validation.header_errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              }
            />
          )}
          {validation.row_errors.length > 0 && (
            <Table
              size="small"
              columns={ROW_ERROR_COLUMNS}
              dataSource={validation.row_errors}
              rowKey="row_number"
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
            />
          )}
          <Space>
            <Button onClick={() => setCurrent(0)}>Back to upload</Button>
          </Space>
        </Space>
      );
    }

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="success"
          showIcon
          message="File validated"
          description={`Compared against current version "${validation.prev_version_name}". Review the changes below, then continue.`}
        />
        {validation.ccr_changed && (
          <Alert
            type="warning"
            showIcon
            message="Currency rates changed"
            description={
              `Rates differ from the current version ` +
              `(USD→EUR ${validation.prev_usd_to_eur_ccr} → ${validation.default_usd_to_eur_ccr}, ` +
              `USD→GBP ${validation.prev_usd_to_gbp_ccr} → ${validation.default_usd_to_gbp_ccr}). ` +
              `Every EUR/GBP value will be recalculated on commit. By default only your real edits are shown below — ` +
              `tick "Include currency recalculation" to see every recalculated cell.`
            }
          />
        )}
        <Checkbox
          checked={compareCurrencies}
          disabled={validating}
          onChange={e => handleToggleCompare(e.target.checked)}
        >
          Include currency recalculation in the diff
          <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
            (off = only the columns you actually edited)
          </Text>
        </Checkbox>
        <Spin spinning={validating} tip="Recomputing diff…">
          {validation.summary && renderSummary(validation.summary)}
        </Spin>
        <Space>
          <Button onClick={() => setCurrent(0)}>Back</Button>
          <Button type="primary" style={{ background: BRAND, borderColor: BRAND }} onClick={() => setCurrent(2)}>
            Continue
          </Button>
        </Space>
      </Space>
    );
  };

  const renderConfirm = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Paragraph type="secondary">
        Name the new version and confirm. The new version becomes active immediately, and
        work-hour settings are copied from the current version.
      </Paragraph>
      <Descriptions size="small" column={1} bordered style={{ maxWidth: 420 }}>
        <Descriptions.Item label="USD → EUR rate">{eurCcr}</Descriptions.Item>
        <Descriptions.Item label="USD → GBP rate">{gbpCcr}</Descriptions.Item>
      </Descriptions>
      <Form form={form} layout="vertical" style={{ maxWidth: 420 }}>
        <Form.Item
          name="version_name"
          label="Version name"
          rules={[{ required: true, message: 'Enter a version name' }]}
        >
          <Input placeholder="PV-DD-MM-YYYY" />
        </Form.Item>
      </Form>
      <Space>
        <Button onClick={() => setCurrent(1)}>Back</Button>
        <Button
          type="primary"
          loading={committing}
          style={{ background: BRAND, borderColor: BRAND }}
          onClick={handleCommit}
        >
          Create Version
        </Button>
      </Space>
    </Space>
  );

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>
        Base Price Versions
      </Title>
      <Paragraph type="secondary">
        Upload an edited "Pricing Details" export to create a new base price version. The file is
        validated, you review what changed, then confirm to create and activate the new version.
      </Paragraph>

      <Steps
        current={current}
        style={{ marginBottom: 24, maxWidth: 720 }}
        items={[
          { title: 'Upload' },
          { title: 'Review' },
          { title: 'Confirm' },
          { title: 'Done' },
        ]}
      />

      <Card style={{ borderRadius: 8 }}>
        {current === 0 && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Dragger
              accept=".xlsx,.csv"
              multiple={false}
              maxCount={1}
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
              onRemove={() => setFileList([])}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag the edited Pricing Details file here</p>
              <p className="ant-upload-hint">Accepts .xlsx or .csv (exported from Data Exports → Pricing Details)</p>
            </Dragger>

            <div>
              <Text strong>Currency conversion rates</Text>
              <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
                Stored as multipliers (e.g. 1/1.16 ≈ 0.862069). EUR/GBP are derived as USD × rate.
                Prefilled from the current version — change them only if FX rates changed.
              </Paragraph>
              <Space wrap>
                <span>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>USD → EUR</Text>
                  <InputNumber
                    min={0}
                    step={0.000001}
                    value={eurCcr}
                    onChange={v => setEurCcr(v ?? undefined)}
                    style={{ width: 180 }}
                  />
                </span>
                <span>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>USD → GBP</Text>
                  <InputNumber
                    min={0}
                    step={0.000001}
                    value={gbpCcr}
                    onChange={v => setGbpCcr(v ?? undefined)}
                    style={{ width: 180 }}
                  />
                </span>
              </Space>
            </div>

            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              loading={validating}
              disabled={!selectedFile || eurCcr == null || gbpCcr == null}
              style={{ background: BRAND, borderColor: BRAND }}
              onClick={() => runValidate(compareCurrencies, true)}
            >
              Validate
            </Button>
          </Space>
        )}

        {current === 1 && renderReview()}

        {current === 2 && renderConfirm()}

        {current === 3 && committed && (
          <Result
            status="success"
            title={`Base price version "${committed.version_name}" created`}
            subTitle={`Version ID ${committed.version_id} is now the active base price version.`}
            extra={[
              <Button
                key="another"
                type="primary"
                style={{ background: BRAND, borderColor: BRAND }}
                onClick={resetAll}
              >
                Create another
              </Button>,
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default BasePriceVersionsTab;
